import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCookies } from "react-cookie";
import SymbolSearchPopup from "./SymbolSearchPopup";
import AdminSharedPresetPanel from "./AdminSharedPresetPanel";
import AdminWzBulkSettingsForm from "./AdminWzBulkSettingsForm";
import {
  getDefaultWzStartSettings,
  mergeWzStartSettings,
} from "./wzStartSettingsShared";

const formatSymbolLabel = (symbol) => {
  if (!symbol) return "";
  const slashIndex = symbol.indexOf("/");
  return slashIndex > 0 ? symbol.substring(0, slashIndex) : symbol;
};

export default function AdminWzBulkStart() {
  const [cookies] = useCookies(["uuid"]);
  const [authChecked, setAuthChecked] = useState(false);
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUids, setSelectedUids] = useState(() => new Set());
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false);
  const [symbolPopupAnchor, setSymbolPopupAnchor] = useState(null);
  const [settings, setSettings] = useState(() => getDefaultWzStartSettings());
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [logs, setLogs] = useState([]);
  const [alsoStartTotalBenefit, setAlsoStartTotalBenefit] = useState(false);

  const appendLog = useCallback((message) => {
    setLogs((prev) => [
      { id: `${Date.now()}-${prev.length}`, message, at: new Date().toLocaleTimeString() },
      ...prev,
    ].slice(0, 200));
  }, []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/admin/wz/bulk_start/members", {
        credentials: "include",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.ok) {
        throw new Error(data.message || "회원 목록을 불러오지 못했습니다.");
      }
      setMembers(data.members || []);
      setSelectedUids(new Set());
    } catch (error) {
      alert(error.message || error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!cookies.uuid) {
      alert("로그인 정보가 없습니다. 관리자 로그인 페이지로 이동합니다.");
      window.location.href = "/AdminLogin";
      return;
    }

    fetch(`/SetPage/admin_user?uuid=${cookies.uuid}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("서버 응답 실패");
        }
        return response.json();
      })
      .then((data) => {
        const user = data[0];
        if (!user) {
          throw new Error("관리자 정보 없음");
        }
        const readOnly = user.admin_possible === "1" || user.admin_possible === 1;
        if (readOnly) {
          alert("읽기 전용 관리자는 이 페이지를 사용할 수 없습니다.");
          window.location.href = "/Adminpage";
          return;
        }
        setAuthChecked(true);
        fetchMembers();
      })
      .catch(() => {
        alert("관리자 인증에 실패했습니다.");
        window.location.href = "/AdminLogin";
      });
  }, [cookies.uuid, fetchMembers]);

  const filteredMembers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return members;
    return members.filter(
      (row) =>
        String(row.uid || "").toLowerCase().includes(query) ||
        String(row.nickname || "").toLowerCase().includes(query)
    );
  }, [members, searchQuery]);

  const allFilteredSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((row) => selectedUids.has(row.uid));

  const toggleSelectAllFiltered = () => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredMembers.forEach((row) => next.delete(row.uid));
      } else {
        filteredMembers.forEach((row) => next.add(row.uid));
      }
      return next;
    });
  };

  const toggleUid = (uid) => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  const applyPreset = (payload = {}) => {
    setSettings((prev) => mergeWzStartSettings(prev, payload));
  };

  const handleOpenSymbolPopup = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const popupWidth = 300;
    const popupHeight = 320;
    const top = Math.max(16, rect.top - popupHeight - 12);
    const left = Math.min(
      Math.max(16, rect.left),
      viewportWidth - popupWidth - 16
    );
    setSymbolPopupAnchor({ top, left });
    setIsSymbolPopupOpen(true);
  };

  const handleCloseSymbolPopup = () => {
    setIsSymbolPopupOpen(false);
    setSymbolPopupAnchor(null);
  };

  const handleBulkStart = async () => {
    const uids = Array.from(selectedUids);
    if (!selectedSymbol) {
      alert("심볼을 먼저 선택해 주세요.");
      return;
    }
    if (uids.length === 0) {
      alert("매매를 시작할 회원을 선택해 주세요.");
      return;
    }

    const confirmed = window.confirm(
      `선택한 ${uids.length}명에게 ${formatSymbolLabel(selectedSymbol)} (${selectedSymbol}) Wizard 매매를 일괄 시작합니다.\n` +
        "봇이 신호에 따라 진입합니다.\n\n계속하시겠습니까?"
    );
    if (!confirmed) return;

    setRunning(true);
    setProgress({ done: 0, total: uids.length, currentUid: "" });
    appendLog(`일괄 매매 시작 (${uids.length}명 · ${selectedSymbol})`);

    let successCount = 0;
    let failCount = 0;

    for (let index = 0; index < uids.length; index += 1) {
      const uid = uids[index];
      setProgress({ done: index, total: uids.length, currentUid: uid });

      try {
        const response = await fetch("/admin/wz/bulk_start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            uid,
            symbol: selectedSymbol,
            settings,
            start_total_benefit: alsoStartTotalBenefit,
          }),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || !data.ok) {
          throw new Error(data.message || "시작 실패");
        }

        let logLine = `[${uid}] ${data.nickname || ""} — ${data.created ? "신규 등록·" : ""}${data.message || "완료"}`;
        if (alsoStartTotalBenefit && data.total_benefit) {
          logLine += data.total_benefit.ok !== false
            ? ` · 종합익절: ${data.total_benefit.message || "시작"}`
            : ` · 종합익절 실패: ${data.total_benefit.message || "오류"}`;
        }
        appendLog(logLine);
        successCount += 1;
      } catch (error) {
        failCount += 1;
        appendLog(`[${uid}] 오류: ${error.message || error}`);
      }

      setProgress({ done: index + 1, total: uids.length, currentUid: uid });
    }

    setRunning(false);
    setProgress(null);
    appendLog(`완료 — 성공 ${successCount}명, 실패 ${failCount}명`);
    alert(`일괄 매매 시작이 끝났습니다.\n성공: ${successCount}명\n실패: ${failCount}명`);
    await fetchMembers();
  };

  const runBulkForUids = async ({ uids, actionLabel, requestBuilder }) => {
    setRunning(true);
    setProgress({ done: 0, total: uids.length, currentUid: "" });
    appendLog(`${actionLabel} (${uids.length}명)`);

    let successCount = 0;
    let failCount = 0;

    for (let index = 0; index < uids.length; index += 1) {
      const uid = uids[index];
      setProgress({ done: index, total: uids.length, currentUid: uid });

      try {
        const response = await requestBuilder(uid);
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.ok) {
          throw new Error(data.message || "처리 실패");
        }
        appendLog(`[${uid}] ${data.nickname || ""} — ${data.message || "완료"}`);
        successCount += 1;
      } catch (error) {
        failCount += 1;
        appendLog(`[${uid}] 오류: ${error.message || error}`);
      }

      setProgress({ done: index + 1, total: uids.length, currentUid: uid });
    }

    setRunning(false);
    setProgress(null);
    appendLog(`완료 — 성공 ${successCount}명, 실패 ${failCount}명`);
    alert(`${actionLabel} 완료\n성공: ${successCount}명\n실패: ${failCount}명`);
    await fetchMembers();
  };

  const handleBulkTotalBenefitStart = async () => {
    const uids = Array.from(selectedUids);
    if (uids.length === 0) {
      alert("회원을 선택해 주세요.");
      return;
    }

    const usdt = parseFloat(settings.total_benefit_usdt);
    if (!Number.isFinite(usdt) || usdt <= 0) {
      alert("종합 익절 셋팅(USDT) 값을 먼저 입력해 주세요.");
      return;
    }

    const confirmed = window.confirm(
      `선택한 ${uids.length}명에게 종합 익절을 시작합니다.\n` +
        `설정 USDT: ${usdt}\n` +
        "실행 중인 Wizard 코인이 있는 회원만 적용됩니다.\n\n계속하시겠습니까?"
    );
    if (!confirmed) return;

    await runBulkForUids({
      uids,
      actionLabel: "일괄 종합 익절 시작",
      requestBuilder: (uid) =>
        fetch("/admin/wz/bulk_total_benefit/start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ uid, usdt }),
        }),
    });
  };

  const handleBulkTotalBenefitCancel = async () => {
    const uids = Array.from(selectedUids);
    if (uids.length === 0) {
      alert("회원을 선택해 주세요.");
      return;
    }

    const confirmed = window.confirm(`선택한 ${uids.length}명의 종합 익절을 취소할까요?`);
    if (!confirmed) return;

    await runBulkForUids({
      uids,
      actionLabel: "일괄 종합 익절 취소",
      requestBuilder: (uid) =>
        fetch("/admin/wz/bulk_total_benefit/cancel", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ uid }),
        }),
    });
  };

  const handleLogout = () => {
    const uuid = cookies.uuid;
    if (!uuid) {
      window.location.href = "/AdminLogin";
      return;
    }
    fetch("/super_logout", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: uuid }),
    }).finally(() => {
      window.location.href = "/AdminLogin";
    });
  };

  if (!authChecked) {
    return (
      <main className="admin-dashboard">
        <section className="admin-dashboard__panel">
          <p className="admin-dashboard__eyebrow">인증 확인 중...</p>
        </section>
      </main>
    );
  }

  return (
    <main className="admin-dashboard admin-wz-bulk admin-wz-bulk-start">
      <div className="admin-dashboard__glare" aria-hidden="true" />
      <section className="admin-dashboard__panel">
        <header className="admin-dashboard__hero">
          <div>
            <p className="admin-dashboard__eyebrow">Wizard admin · Bulk start</p>
            <h1 className="admin-dashboard__title">WZ 코인 일괄 매매 시작</h1>
            <p className="admin-dashboard__subtitle">
              Bitget 심볼 선택 후, Wizard(WZ) 설정을 저장하고 위자드(WZ)로 봇을 가동합니다.
            </p>
          </div>
          <div className="admin-dashboard__hero-actions">
            <Link to="/AdminWz" className="admin-dashboard__btn admin-dashboard__btn--ghost">
              Wizard 관리
            </Link>
            <button
              type="button"
              className="admin-dashboard__btn admin-dashboard__btn--danger"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </header>

        <div className="admin-wz-bulk__stack">
          <article className="admin-wz-bulk__card admin-wz-bulk__card--wide">
            <header className="admin-wz-bulk__card-head">
              <span className="admin-wz-bulk__step">1</span>
              <div>
                <h2 className="admin-wz-bulk__card-title">심볼 선택</h2>
                <p className="admin-wz-bulk__card-desc">Bitget 선물 심볼 (일괄 매매 시작 시 필요)</p>
              </div>
            </header>
            <div
              className={`admin-wz-bulk__symbol-pick${
                selectedSymbol ? " is-selected" : ""
              }`}
            >
              <span className="admin-wz-bulk__symbol-value">
                {selectedSymbol
                  ? `${formatSymbolLabel(selectedSymbol)} · ${selectedSymbol}`
                  : "심볼 미선택"}
              </span>
              <button
                type="button"
                className="admin-dashboard__btn admin-dashboard__btn--ghost admin-wz-bulk__symbol-btn"
                onClick={handleOpenSymbolPopup}
                disabled={running}
              >
                심볼 검색/선택
              </button>
            </div>
          </article>
          <SymbolSearchPopup
            isOpen={isSymbolPopupOpen}
            anchorPosition={symbolPopupAnchor}
            onAdd={(sym) => {
              setSelectedSymbol(sym);
              handleCloseSymbolPopup();
            }}
            onClose={handleCloseSymbolPopup}
          />

          <article className="admin-wz-bulk__card admin-wz-bulk__card--wide">
            <header className="admin-wz-bulk__card-head">
              <span className="admin-wz-bulk__step">2</span>
              <div>
                <h2 className="admin-wz-bulk__card-title">Wizard 설정</h2>
                <p className="admin-wz-bulk__card-desc">선택 회원·심볼에 일괄 적용 (위자드(WZ) 동일)</p>
              </div>
            </header>
            <AdminSharedPresetPanel
              isAdminAccess
              compact
              featureKey="wz"
              buildPresetPayload={() => ({ ...settings })}
              applyPreset={applyPreset}
              request={(url, options) => fetch(url, { credentials: "include", ...options })}
            />
            <p className="admin-wz-bulk-start__hint">
              프리셋으로 전체 필드를 채울 수 있습니다.
              <strong> 종합 익절만</strong> 사용할 때는 아래 「종합 익절」 블록의 USDT만 입력하면 됩니다.
            </p>
            <AdminWzBulkSettingsForm
              settings={settings}
              setSettings={setSettings}
              disabled={running}
            />
          </article>

          <article className="admin-wz-bulk__card admin-wz-bulk__card--wide">
            <header className="admin-wz-bulk__card-head">
              <span className="admin-wz-bulk__step">3</span>
              <div>
                <h2 className="admin-wz-bulk__card-title">회원 선택</h2>
                <p className="admin-wz-bulk__card-desc">Wizard(WZ) 승인 회원</p>
              </div>
            </header>

            <div className="admin-wz-bulk__toolbar">
              <div className="admin-wz-bulk__search">
                <input
                  type="text"
                  className="admin-wz-bulk__search-input"
                  placeholder="UID 또는 닉네임 검색"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={running}
                />
                <span className="admin-wz-bulk__search-meta">
                  {filteredMembers.length}명 · 선택 {selectedUids.size}명
                </span>
              </div>
              <button
                type="button"
                className="admin-dashboard__btn admin-dashboard__btn--ghost admin-wz-bulk__refresh-btn"
                onClick={fetchMembers}
                disabled={loading || running}
              >
                {loading ? "불러오는 중..." : "목록 새로고침"}
              </button>
            </div>

            <div className="admin-wz-bulk__action-groups">
              <div className="admin-wz-bulk__action-group">
                <span className="admin-wz-bulk__action-group-label">매매 시작</span>
                <button
                  type="button"
                  className="admin-dashboard__btn admin-dashboard__btn--approve"
                  onClick={handleBulkStart}
                  disabled={running || selectedUids.size === 0 || !selectedSymbol}
                >
                  {running ? "시작 진행 중..." : "일괄 매매 시작"}
                </button>
                <label className="admin-wz-bulk-start__checkbox">
                  <input
                    type="checkbox"
                    checked={alsoStartTotalBenefit}
                    onChange={(e) => setAlsoStartTotalBenefit(e.target.checked)}
                    disabled={running}
                  />
                  성공 시 종합 익절도 시작
                </label>
              </div>
              <div className="admin-wz-bulk__action-group admin-wz-bulk__action-group--accent">
                <span className="admin-wz-bulk__action-group-label">종합 익절</span>
                <button
                  type="button"
                  className="admin-dashboard__btn admin-dashboard__btn--ghost"
                  onClick={handleBulkTotalBenefitStart}
                  disabled={running || selectedUids.size === 0}
                >
                  시작
                </button>
                <button
                  type="button"
                  className="admin-dashboard__btn admin-dashboard__btn--ghost"
                  onClick={handleBulkTotalBenefitCancel}
                  disabled={running || selectedUids.size === 0}
                >
                  취소
                </button>
              </div>
            </div>

            {progress ? (
              <div className="admin-wz-bulk__progress" role="status">
                <strong>
                  진행 {progress.done} / {progress.total}
                </strong>
                {progress.currentUid ? <span> — 현재 UID: {progress.currentUid}</span> : null}
              </div>
            ) : null}

            <div className="admin-dashboard__table-wrapper admin-wz-bulk__table-wrap">
            <table className="admin-dashboard__table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAllFiltered}
                      disabled={running || filteredMembers.length === 0}
                      aria-label="현재 목록 전체 선택"
                    />
                  </th>
                  <th>UID</th>
                  <th>닉네임</th>
                  <th>활성 코인 수</th>
                  <th>코인 목록</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="admin-dashboard__empty">
                        {loading ? "불러오는 중..." : "Wizard 권한 회원이 없습니다."}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((row) => (
                    <tr key={row.uid}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUids.has(row.uid)}
                          onChange={() => toggleUid(row.uid)}
                          disabled={running}
                        />
                      </td>
                      <td>{row.uid}</td>
                      <td>{row.nickname}</td>
                      <td>{row.active_coin_count ?? 0}</td>
                      <td className="admin-wz-bulk__symbols">
                        {(row.symbols || []).map((symbol) => formatSymbolLabel(symbol)).join(", ") || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </article>
        </div>

        {logs.length > 0 ? (
          <div className="admin-wz-bulk__log">
            <h2 className="admin-wz-bulk__log-title">작업 로그</h2>
            <ul>
              {logs.map((entry) => (
                <li key={entry.id}>
                  <span>{entry.at}</span> {entry.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </main>
  );
}
